// =============================================================================
// gridStyles.js
// Shared visual tokens + cell-builder helpers for all Mantle ReactGrid grids.
// No behaviour here — just "how a Mantle grid looks" and small functions that
// stamp out correctly-styled cells so each grid doesn't re-type style objects.
//
// Usage:
//   import { GRID, cell } from '../styles/gridStyles';
//   cell.header('GL Account')
//   cell.label(`${code} - ${desc}`, { zebra })
//   cell.number(value, { editable: true, zebra })
//   cell.aggregate(sum, { kind: 'year' })
//   cell.totalLabel('TOTAL') / cell.totalBlank() / cell.totalNumber(val)
// =============================================================================

// ---- Raw tokens (single source of truth for colour/typography) --------------
export const GRID = {
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
  numFont: "'SF Mono', 'Roboto Mono', Consolas, monospace",

  // Colours reference CSS variables (defined in index.css) so the grids
  // recolour live when the `.dark` class is toggled — see the grid palette
  // block in index.css for the light/dark values.
  color: {
    headerBg: 'var(--grid-header-bg)',
    headerText: 'var(--grid-header-text)',
    quarterHeaderBg: 'var(--grid-quarter-header-bg)',

    text: 'var(--grid-text)',
    subtext: 'var(--grid-subtext)',
    muted: 'var(--grid-muted)',
    negative: 'var(--grid-negative)',

    zebraEven: 'var(--grid-zebra-even)',
    zebraOdd: 'var(--grid-zebra-odd)',

    readonlyBg: 'var(--grid-readonly-bg)',
    yearAggBg: 'var(--grid-year-agg-bg)',
    quarterAggBg: 'var(--grid-quarter-agg-bg)',

    // accents for the "method" style cells
    manualText: 'var(--grid-manual-text)',
    manualBg: 'var(--grid-manual-bg)',
    calcText: 'var(--grid-calc-text)',
    calcBg: 'var(--grid-calc-bg)',

    // total row
    totalBg: 'var(--grid-total-bg)',
    totalText: 'var(--grid-total-text)',
    totalNegative: 'var(--grid-total-negative)',

    borderSoft: 'var(--grid-border-soft)',
    borderMid: 'var(--grid-border-mid)',
    borderStrong: 'var(--grid-border-strong)',
    borderDivider: 'var(--grid-border-divider)',
  },

  size: {
    headerRow: 36,
    dataRow: 30,
    totalRow: 32,
    fontData: 11,
    fontMeta: 10,
  },
};

const c = GRID.color;

// ---- Base style fragments (composed by the helpers below) -------------------
export const HEADER_STYLE = {
  background: c.headerBg,
  color: c.headerText,
  border: { bottom: { color: c.headerBg, style: 'solid', width: '1px' } },
};

export const CLICK_HEADER = {
  ...HEADER_STYLE,
  cursor: 'pointer',
  justifyContent: 'space-between',
  paddingLeft: 10,
  paddingRight: 10,
};

export const YEAR_HEADER = { ...CLICK_HEADER, background: c.headerBg, fontWeight: '700' };
export const Q_HEADER = { ...CLICK_HEADER, background: c.quarterHeaderBg };

const zebraOf = (idx) => (idx % 2 === 0 ? c.zebraEven : c.zebraOdd);

// ---- Cell builders ----------------------------------------------------------
// Each returns a ReactGrid cell object. Callers pass content + light options.
export const cell = {
  // Plain black column header. Pass { align: 'right' } for numeric headers.
  header(text, { align = 'left', style } = {}) {
    return {
      type: 'header',
      text,
      style: {
        ...HEADER_STYLE,
        ...(align === 'right' ? { justifyContent: 'flex-end' } : {}),
        ...style,
      },
    };
  },

  // Clickable year/quarter header (drill-down). variant: 'year' | 'quarter'.
  drillHeader(text, variant) {
    return {
      type: 'header',
      text,
      style: variant === 'year' ? YEAR_HEADER : Q_HEADER,
    };
  },

  // Sticky-left label cell (e.g. "5000 - Rent Expense"). idx drives zebra.
  // align: 'left' (default) | 'right' — right pushes text to the cell's end.
  label(text, { idx = 0, bold = true, divider = true, align = 'left' } = {}) {
    return {
      type: 'text',
      text,
      nonEditable: true,
      style: {
        background: zebraOf(idx),
        color: c.text,
        fontWeight: bold ? '600' : '400',
        ...(align === 'right'
          ? { justifyContent: 'flex-end', paddingRight: 12 }
          : { paddingLeft: 12 }),
        ...(divider
          ? { border: { right: { color: c.borderMid, style: 'solid', width: '1px' } } }
          : {}),
      },
    };
  },

  // Secondary text cell (cost centre, source, etc.).
  text(text, { idx = 0, color = c.subtext, paddingLeft = 8 } = {}) {
    return {
      type: 'text',
      text: text ?? '',
      nonEditable: true,
      style: { background: zebraOf(idx), color, paddingLeft },
    };
  },

  // Editable free-text cell (e.g. a UoM the user types). Null/undefined shows
  // as blank. Editable because nonEditable is not set.
  editText(text, { idx = 0, align = 'left' } = {}) {
    return {
      type: 'text',
      text: text ?? '',
      style: {
        background: zebraOf(idx),
        color: c.text,
        ...(align === 'right'
          ? { justifyContent: 'flex-end', paddingRight: 8 }
          : { paddingLeft: 8 }),
      },
    };
  },

  // Editable / read-only numeric value cell.
  // `accent` (a hex colour) flags a cell holding a non-base currency (its
  // currency's display_color): the text is recoloured AND the left border is
  // thickened & tinted, so a different currency reads clearly. Negative values
  // still show the negative colour (correctness wins over the currency tint).
  number(value, { editable = false, idx = 0, accent } = {}) {
    return {
      type: 'number',
      value: value || 0,
      nonEditable: !editable,
      style: {
        background: editable ? zebraOf(idx) : c.readonlyBg,
        color: value < 0 ? c.negative : (accent || c.text),
        fontWeight: accent ? '700' : undefined,
        fontFamily: GRID.numFont,
        fontSize: GRID.size.fontData,
        justifyContent: 'flex-end',
        paddingRight: 10,
        border: { left: { color: accent || c.borderSoft, style: 'solid', width: accent ? '3px' : '1px' } },
      },
    };
  },

  // Read-only aggregate cell (year/quarter rollup). kind: 'year' | 'quarter'.
  aggregate(value, { kind = 'quarter' } = {}) {
    const isYear = kind === 'year';
    return {
      type: 'number',
      value: value || 0,
      nonEditable: true,
      style: {
        background: isYear ? c.yearAggBg : c.quarterAggBg,
        color: value < 0 ? c.negative : c.text,
        fontFamily: GRID.numFont,
        fontSize: GRID.size.fontData,
        fontWeight: '600',
        justifyContent: 'flex-end',
        paddingRight: 10,
        border: { left: { color: c.borderStrong, style: 'solid', width: '1px' } },
      },
    };
  },

  // "Method"-style tag cell (active vs muted accent). Generic two-state pill.
  tag(text, { active = false, activeColor = c.manualText, activeBg = c.manualBg,
              mutedColor = c.calcText, mutedBg = c.calcBg, clickable = false } = {}) {
    return {
      type: 'text',
      text,
      nonEditable: true,
      style: {
        color: active ? activeColor : mutedColor,
        background: active ? activeBg : mutedBg,
        fontWeight: '600',
        fontSize: GRID.size.fontMeta,
        paddingLeft: 8,
        cursor: clickable ? 'pointer' : 'default',
      },
    };
  },

  // ReactGrid dropdown cell. `values` is [{ value, label }]. `isOpen` must be
  // driven from component state (ReactGrid reports open/close via onCellsChanged;
  // the caller persists it and re-renders so the menu stays open).
  dropdown(selectedValue, values, { idx = 0, isOpen = false } = {}) {
    return {
      type: 'dropdown',
      selectedValue: selectedValue || undefined,
      values,
      isOpen,
      style: { background: zebraOf(idx), color: c.text },
    };
  },

  // ---- Total row cells ----
  totalLabel(text) {
    return {
      type: 'header',
      text,
      style: {
        background: c.totalBg, color: c.totalText, fontWeight: '700', paddingLeft: 12,
        border: { top: { color: c.totalBg, style: 'solid', width: '2px' } },
      },
    };
  },

  totalBlank() {
    return {
      type: 'header',
      text: '',
      style: {
        background: c.totalBg,
        border: { top: { color: c.totalBg, style: 'solid', width: '2px' } },
      },
    };
  },

  totalNumber(value) {
    return {
      type: 'number',
      value: value || 0,
      nonEditable: true,
      style: {
        background: c.totalBg,
        color: value < 0 ? c.totalNegative : c.totalText,
        fontFamily: GRID.numFont,
        fontSize: GRID.size.fontData,
        fontWeight: '700',
        justifyContent: 'flex-end',
        paddingRight: 10,
        border: {
          top: { color: c.totalBg, style: 'solid', width: '2px' },
          left: { color: c.borderDivider, style: 'solid', width: '1px' },
        },
      },
    };
  },
};

export default { GRID, cell, HEADER_STYLE, YEAR_HEADER, Q_HEADER, CLICK_HEADER };